import { styled } from '@mui/material/styles'
import { FC } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  TablePagination,
} from '@mui/material'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import RoomServiceIcon from '@mui/icons-material/RoomService'
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox'
import FolderSharedIcon from '@mui/icons-material/FolderShared'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useState, useMemo } from 'react'
import { useGetList, useRedirect } from 'react-admin'
import { xxllncColor } from '../../layout/themes'

const PREFIX = 'xxllncDashboard'

const classes = {
  root: `${PREFIX}-root`,
  tableContainer: `${PREFIX}-tableContainer`,
  waitBadge: `${PREFIX}-waitBadge`,
}

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),

  [`& .${classes.tableContainer}`]: {
    borderRadius: '16px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  },

  [`& .${classes.waitBadge}`]: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    borderRadius: '20px',
    fontWeight: 'bold',
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: 'fit-content',
  },
}))

interface ConversationRecord {
  id: string;
  created: string;
  subject: string | null;
  contact_name: string | null;
  employee_email: string;
  channel: string;
  status: string;
}

type Order = 'asc' | 'desc';

const channelIcons: Record<string, JSX.Element> = {
  phone: <LocalPhoneIcon fontSize="small" />,
  counter: <RoomServiceIcon fontSize="small" />,
  mail: <MarkunreadMailboxIcon fontSize="small" />,
  person: <FolderSharedIcon fontSize="small" />,
  email: <AlternateEmailIcon fontSize="small" />,
  social: <ThumbUpIcon fontSize="small" />,
}

const getWachttijdStyle = (dagen: number) => {
  if (dagen >= 15) return { bg: xxllncColor.tallPoppy, textColor: '#fff' }
  if (dagen >= 10)
    return { bg: xxllncColor.coral40, textColor: xxllncColor.black }
  if (dagen >= 7)
    return { bg: xxllncColor.yellow40, textColor: xxllncColor.black }
  return { bg: xxllncColor.lightblue20, textColor: xxllncColor.black }
}

const Dashboard: FC = () => {
  const redirect = useRedirect()
  const [order, setOrder] = useState<Order>('desc')
  const [orderBy, setOrderBy] = useState<string>('created')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(8)

  const { data, isLoading } = useGetList<ConversationRecord>('conversation', {
    pagination: { page: 1, perPage: 25 },
    sort: { field: 'created', order: 'DESC' },
    filter: { status: 'active' },
  })

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const paginatedData = useMemo(() => {
    if (!data) return []

    const sorted = [...data].sort((a, b) => {
      const aValue = a[orderBy as keyof ConversationRecord] ?? ''
      const bValue = b[orderBy as keyof ConversationRecord] ?? ''
      if (bValue < aValue) return order === 'desc' ? -1 : 1
      if (bValue > aValue) return order === 'desc' ? 1 : -1
      return 0
    })

    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }, [data, order, orderBy, page, rowsPerPage])

  const getDagen = (createdAt: string) => {
    if (!createdAt) return 0
    return Math.floor(
      (new Date().getTime() - new Date(createdAt).getTime()) /
        (1000 * 60 * 60 * 24),
    )
  }

  if (isLoading) return null

  return (
    <StyledBox>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
        Openstaande terugbelverzoeken
      </Typography>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'created'}
                  direction={orderBy === 'created' ? order : 'asc'}
                  onClick={() => handleSort('created')}
                >
                  Wachttijd
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'subject'}
                  direction={orderBy === 'subject' ? order : 'asc'}
                  onClick={() => handleSort('subject')}
                >
                  Onderwerp
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'employee_email'}
                  direction={orderBy === 'employee_email' ? order : 'asc'}
                  onClick={() => handleSort('employee_email')}
                >
                  Medewerker
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'channel'}
                  direction={orderBy === 'channel' ? order : 'asc'}
                  onClick={() => handleSort('channel')}
                >
                  Kanaal
                </TableSortLabel>
              </TableCell>

              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => {
              const dagen = getDagen(row.created)
              const style = getWachttijdStyle(dagen)
              return (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => redirect('edit', 'conversation', row.id)}
                >
                  <TableCell>
                    <Box
                      className={classes.waitBadge}
                      sx={{
                        bgcolor: style.bg,
                        color: style.textColor,
                      }}
                    >
                      {dagen} {dagen === 1 ? 'dag' : 'dagen'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: '250px' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.subject || ''}
                    >
                      {row.subject || 'Geen onderwerp'}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.employee_email}</TableCell>
                  <TableCell>
                    {channelIcons[row.channel] || row.channel}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small">
                      <ChevronRightIcon sx={{ color: xxllncColor.blue100 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rijen per pagina:"
        />
      </TableContainer>
    </StyledBox>
  )
}

export default Dashboard
